import {
	Component, Input, OnDestroy, OnInit, Output,
	EventEmitter, Renderer2, HostListener
} from '@angular/core';
import { Subscription } from 'rxjs';
import { Registration, RegistrationState } from './definitions';
import { RegistrationService } from './registration.service';
import { KngLoaderState } from 'kinetic-angular';
import { LoggerService } from '../../services/logger/logger.service';
import { Icons, UserType } from '../../services/shared/shared-enums';
import { SharedService } from '../../services/shared/shared.service';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { LocalStorageService } from '../../services/local-storage/local-storage.service';
import { Router } from '@angular/router';
import { RegistrationFactory } from './registration.factory';
import { get as lodashGet } from 'lodash';
import { RegistrationUtilService } from './registration-util.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { AnalyticsDataLayer } from '../../services/analytics/definitions';
@Component({
	selector: 'tmhna-portal-registration',
	templateUrl: './template/registration.component.html',
	styleUrls: ['./template/registration.component.scss']
})
export class RegistrationComponent implements Registration, OnInit, OnDestroy
{
	private readonly subscriptions: Subscription = new Subscription();
	
	loading: boolean = false;
	
	@Input() set state(value: RegistrationState)
	{
		this.service.updateState(value);
	}
	get state(): RegistrationState
	{
		return this.service.state;
	}

	public loaderState: KngLoaderState = {
		imagePath: Icons.loaderWhite
	};
	@Output() stateChanged: EventEmitter<RegistrationState> = new EventEmitter<RegistrationState>();

	constructor(public service: RegistrationService, private readonly loggerService: LoggerService, 
		public sharedService: SharedService, private readonly authService: AuthenticationService, private readonly localStorageService: LocalStorageService, private readonly registrationUtilService: RegistrationUtilService, private readonly router: Router,
		private readonly renderer2: Renderer2, private readonly analyticsService: AnalyticsService)
	{}

	ngOnInit()
	{
		this.service.canActivatePortal()?.then(()=>
		{
			this.service.validUserSuccess = true;
			this.service.pageLoadHandler();
		}
		).catch(error => 
		{
			this.service.validUserSuccess = true;
			this.service.pageLoadHandler();
			this.loggerService.error(error);
		});
		const ele: HTMLElement = this.renderer2.selectRootElement('html', true);
		const value = ele.getAttribute('lang');
		if(value)
		{
			this.localStorageService.setLanguage(value);
		}
		else
		{
			this.localStorageService.setLanguage('en');
		}
		this.service.getFooterData();
		this.service.addLabelsToForm();
		this.initSubscriptions();
	}
	
	@HostListener('window:beforeunload', ['$event'])
	onBackButtonClick() 
	{
		this.router.navigate(['/auth']);
	}
	ngOnDestroy(): void
	{
		this.destroySubscriptions();
		this.service.state.loading = false;
	}

	private initSubscriptions(): void
	{
		const stateChangeSubs: Subscription = this.service.stateChanged.subscribe(
			(next: RegistrationState) => this.stateChanged.emit(next)
		);
		this.subscriptions.add(stateChangeSubs);
	}
	private destroySubscriptions(): void
	{
		this.subscriptions.unsubscribe();
	}

	logout()
	{
		this.authService.logout()?.catch(error => 
		{
			this.loggerService.error(error);
		});
		this.localStorageService.clearStorage();
	}

	notifyLanguageChange(selection: string): void
	{
		const translationValues: Map<string, string> = RegistrationFactory.buildDefaultTranslationValuesMap();
		const selectionId: string | undefined = translationValues.get(selection);
		if (selectionId === undefined)
		{
			throw new Error(`Can't translate to "${selection}". Supported values are:\n${JSON.stringify(translationValues)}`);
		}
		this.loading = true;
		const panelAfterFirstTranslation: HTMLIFrameElement | null = this.service.getGoogleTranslatePanel(this.renderer2);
		const isValidSecondTimeTranslation: boolean = lodashGet(panelAfterFirstTranslation, 'contentWindow.document', undefined) !== undefined;
		isValidSecondTimeTranslation ?
			this.service.performSubsequetTranslation(panelAfterFirstTranslation as HTMLIFrameElement, selectionId) :
			this.service.performFirstTimeTranslation(selectionId, this.renderer2);
		this.localStorageService.setLanguage(selectionId);
		setTimeout(() =>
		{
			this.loading = false;
		}, 500);
	}

	isTermsandPrivacyAgreed(event: boolean)
	{
		this.service.termsandPrivacyAgreed = event;
		if(this.service.termsandPrivacyAgreed)
		{
			this.state.submitButton.disabled = false;
			this.state.continueButton.disabled = false;
		}
		else
		{
			this.state.submitButton.disabled = true;
			this.state.continueButton.disabled = true;
		}
	}
	displayContinueButton(event: boolean)
	{
		if(event === true)
		{
			this.service.isContinueButton = true;
			this.service.isSubmitRegistrationButton = false;
			if(this.service.termsandPrivacyAgreed)
			{
				this.state.continueButton.disabled = false;
			}
		}
		else
		{
			this.service.isContinueButton = false;
			this.service.isSubmitRegistrationButton = true;
		}
	}

	onSubmitRegistration()
	{
		this.trackAnalyticsOnSubmit();
		this.state.loading = true;
		if(this.service.userType === UserType.generalUser)
		{
			this.service.saveGeneralUser();
		}
		else if(this.service.userType === UserType.individualUser)
		{
			this.service.saveIndividualUser();
		}
		else if(this.service.userType === UserType.businessAdminUser && this.service.isSubmitRegistrationButton)
		{
			this.service.saveIndividualUser();
		}
	}

	onContinue()
	{
		this.trackAnalyticsOnContinue();
		this.service.saveBusinessUser();
	}

	trackAnalyticsOnSubmit()
	{
		const analyticsData: AnalyticsDataLayer = {
			event: 'Submit Registration',
			link: {
				name: 'Submit Registration button'
			}
		};
		this.analyticsService.trackRegistrationByEvent(analyticsData, this.state.email);
	}
	trackAnalyticsOnContinue()
	{
		const analyticsData: AnalyticsDataLayer = {
			event: 'Continue Registration',
			link: {
				name: 'Continue Button'
			}
		};
		this.analyticsService.trackRegistrationByEvent(analyticsData, this.state.email);
	}
}
